import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { apiFetch, callApi } from '../api';
import Modal from '../components/Modal';
import { useConfirm } from '../components/ConfirmDialog';
import {
  Badge, CardSkeleton, EmptyState, Field, Icon, PageHeader, Pagination, SearchInput, Switch,
  TableSkeleton, cx,
} from '../components/ui';

/* ── Data governance ──────────────────────────────────────────────────────────
   An operator sets, per CATEGORY of data, how many days it is kept and how often the
   sweep runs it (backend/services/retention_catalog.py). Every table the platform keeps
   is either in a category or declared bounded in that catalog, with the reason, and a
   test fails on a table in neither. Each category's runs are recorded like any other job
   (Platform, Jobs), and this page reads its last run from there.

   Laid out as a policy list, the shape retention consoles share: one row per category
   with what it keeps, for how long, what happens at the end and when it last ran, so the
   whole policy reads at a glance. The settings of one category open in a dialog, and the
   two actions that cannot be undone sit together at the bottom. */

// What happens past the window. The words match the catalog's `fate`.
const FATE = {
  aggregate: { tone: 'accent', label: 'Rolled up, then deleted' },
  compact: { tone: 'info', label: 'Compacted to daily' },
  delete: { tone: 'neutral', label: 'Deleted' },
  wipe: { tone: 'danger', label: 'Hard delete' },
};

const EVERY = [
  [1, 'Every hour'], [3, 'Every 3 hours'], [6, 'Every 6 hours'], [12, 'Every 12 hours'],
  [24, 'Daily'], [48, 'Every 2 days'], [168, 'Weekly'], [336, 'Every 2 weeks'], [720, 'Every 30 days'],
];

const RUN_STATUS = {
  ok: { tone: 'ok', label: 'OK' },
  error: { tone: 'danger', label: 'Failed' },
  skipped: { tone: 'neutral', label: 'Skipped' },
  running: { tone: 'info', label: 'Running' },
  interrupted: { tone: 'warn', label: 'Interrupted' },
};

// The figures of a run worth a glance, in the order they read best. A rollup row is one
// day of one aggregate, not a source row: "rows deleted" were all rolled up first.
const FIGURES = [
  ['rows_deleted', 'rows deleted'], ['rollup_rows', 'daily rollup rows written'],
  ['inactive_wiped', 'inactive accounts wiped'], ['deleted_records_removed', 'deleted-account records removed'],
  ['accounts_skipped', 'left for the next run'], ['failed', 'failed'],
];

const DAY_MS = 24 * 60 * 60 * 1000;
const PAGE_SIZE = 10;
const POLL_MS = 4000;
const POLL_FOR_MS = 10 * 60 * 1000;

// "19 Jul 2026", as on the Users page: a month name cannot be misread as D/M vs M/D.
function formatDay(value) {
  return new Date(value).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatWhen(iso) {
  return new Date(iso).toLocaleString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function formatShort(iso) {
  return new Date(iso).toLocaleString(undefined, {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

function ago(iso) {
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms)) return null;
  const min = Math.round(ms / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const h = Math.round(min / 60);
  if (h < 48) return `${h} h ago`;
  return `${Math.round(h / 24)} days ago`;
}

function validDays(value, max) {
  const days = Number(value);
  return Number.isInteger(days) && days >= 1 && days <= max ? days : null;
}

function span(days) {
  if (days >= 730) return `about ${Math.round(days / 365)} years`;
  if (days >= 60) return `about ${Math.round(days / 30.4)} months`;
  if (days >= 14) return `about ${Math.round(days / 7)} weeks`;
  return null;
}

// Where the window falls, as a date, and how long it is in words. A category floored by
// another's window (`at_least`) is described with the window its runs apply.
function windowHint(cat, typed) {
  const floor = cat.at_least?.retention_days || 0;
  const days = Math.max(typed, floor);
  const day = formatDay(Date.now() - days * DAY_MS);
  const length = span(days) ? `, ${span(days)}` : '';
  const floored = floor > typed ? `, the ${cat.at_least.label} window` : '';
  if (cat.key === 'accounts') return `Wipes accounts idle since before ${day}${length}`;
  if (cat.key === 'deleted_accounts') return `Removes records of accounts deleted before ${day}${length}${floored}`;
  if (cat.fate === 'compact') return `Keeps full detail from ${day}${length}`;
  return `Keeps data from ${day}${length}`;
}

// The same fact, short enough for a table cell.
function sinceText(cat) {
  const day = formatDay(Date.now() - (cat.effective_days || cat.retention_days) * DAY_MS);
  if (cat.key === 'accounts') return `idle since before ${day}`;
  if (cat.key === 'deleted_accounts') return `deleted before ${day}`;
  if (cat.fate === 'compact') return `full detail since ${day}`;
  return `since ${day}`;
}

function everyOptions(current) {
  if (EVERY.some(([h]) => h === current)) return EVERY;
  return [...EVERY, [current, `Every ${current} hours`]].sort((a, b) => a[0] - b[0]);
}

const everyLabel = (hours) => (EVERY.find(([h]) => h === hours) || [hours, `Every ${hours} hours`])[1];

const editOf = (cat) => ({
  retention_days: String(cat.retention_days), every_hours: cat.every_hours, enabled: cat.enabled,
});

// "Closed channel links" or "Service broadcasts, Teams dead letters and Ended schedules".
function listOf(labels) {
  if (labels.length < 2) return labels.join('');
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
}

function runFigures(run) {
  const summary = run?.summary || {};
  // A zero is only worth saying for the rows deleted: "0 failed" is noise.
  return FIGURES.filter(([k]) => typeof summary[k] === 'number' && (summary[k] > 0 || k === 'rows_deleted'))
    .map(([k, label]) => `${summary[k].toLocaleString()} ${label}`);
}

function RunWhen({ run }) {
  return run.started_at
    ? <time dateTime={run.started_at} title={formatWhen(run.started_at)}>{ago(run.started_at)}</time>
    : null;
}

// The last run that did its work, and under it a newer skip (another run was going, the
// freeze was on), so a skip never hides the result of the run that did happen.
function LastRun({ run, skip }) {
  if (!run && !skip) return <span className="text-faint">Not run yet</span>;
  const st = run && (RUN_STATUS[run.status] || { tone: 'neutral', label: run.status });
  return (
    <span className="stack gap-1">
      {run && (
        <span className="row row-wrap gap-2">
          <Badge tone={st.tone} dot>{st.label}</Badge>
          <RunWhen run={run} />
          {run.trigger === 'manual' && <span className="text-muted">run by hand</span>}
          {runFigures(run).map((f) => <span key={f} className="text-muted num">{f}</span>)}
        </span>
      )}
      {run?.error && <span className="text-sm text-danger">{run.error}</span>}
      {skip && (
        <span className="row row-wrap gap-2 text-sm">
          <Badge tone={RUN_STATUS.skipped.tone} dot>{RUN_STATUS.skipped.label}</Badge>
          <RunWhen run={skip} />
          {skip.trigger === 'manual' && <span className="text-muted">run by hand</span>}
          {skip.summary?.skipped && <span className="text-muted">{skip.summary.skipped}</span>}
        </span>
      )}
    </span>
  );
}

// The table's version: the status and when, then the one figure that matters or the error.
function LastRunCell({ run, skip }) {
  if (!run && !skip) return <span className="text-faint">Not run yet</span>;
  const shown = run || skip;
  const st = RUN_STATUS[shown.status] || { tone: 'neutral', label: shown.status };
  const sub = run?.error || runFigures(run)[0] || (skip && !run ? skip.summary?.skipped : null);
  return (
    <div className="stack gap-1" style={{ alignItems: 'flex-start' }}>
      <span className="row gap-2" style={{ whiteSpace: 'nowrap' }}>
        <Badge tone={st.tone} dot>{st.label}</Badge>
        <span className="text-sm"><RunWhen run={shown} /></span>
      </span>
      {sub && <span className={cx('text-sm', run?.error ? 'text-danger' : 'text-muted')}>{sub}</span>}
    </div>
  );
}

// After "Count what is due": what the category's next run removes, and apart from it the
// rows of tables still waiting for a confirmation. {n, noun, waiting, error} or null.
function dueOf(cat, counted) {
  if (!counted) return null;
  if (counted.error) return { error: counted.error };
  if (cat.key === 'accounts') {
    const n = (counted.users_to_wipe || []).length;
    return { n, noun: n === 1 ? 'account' : 'accounts', perRun: counted.per_run, users: counted.users_to_wipe };
  }
  if (cat.key === 'deleted_accounts') {
    const n = counted.deleted_records || 0;
    return { n, noun: n === 1 ? 'record' : 'records', perRun: counted.per_run };
  }
  const held = new Set((cat.held || []).map((h) => h.table));
  let n = 0;
  let waiting = 0;
  cat.members.forEach((m) => {
    const rows = counted.tables?.[m.table];
    if (typeof rows !== 'number') return;
    if (held.has(m.table)) waiting += rows;
    else n += rows;
  });
  return { n, noun: n === 1 ? 'row' : 'rows', waiting };
}

function DueText({ due }) {
  if (!due) return null;
  if (due.error) return <span className="text-sm text-danger">Not counted: {due.error}</span>;
  return (
    <span className="text-sm">
      <strong className="num">{due.n.toLocaleString()}</strong> {due.noun}
      {due.perRun && due.n > due.perRun ? <span className="text-muted">, at most {due.perRun} a run</span> : null}
      {due.waiting > 0 && <span className="text-muted">, {due.waiting.toLocaleString()} waiting for confirmation</span>}
    </span>
  );
}

/* A category holding tables no per-table setting swept: the switch to categories must not
   delete what was kept forever without an operator saying so. */
function HeldNotice({ cat, onConfirm, confirming }) {
  const held = cat.held || [];
  if (!held.length) return null;
  const all = held.length === cat.members.length;
  const labels = held.map((h) => h.label);
  return (
    <div className="notice notice-warn" role="status">
      <Icon name="alert" />
      <div className="notice-body">
        <div className="notice-title">
          {all ? 'Not running until you confirm it' : `${listOf(labels)}: not swept until you confirm`}
        </div>
        Never cleaned before, so {held.length === 1 ? 'it waits' : 'they wait'} for you. Count what is due first.
      </div>
      <button type="button" className="btn btn-secondary btn-sm" onClick={onConfirm} disabled={confirming}>
        {confirming ? <span className="spinner-sm" /> : <Icon name="check" />}
        Confirm
      </button>
    </div>
  );
}

/* One category's settings: what it covers, its window, how often it runs, the schedule
   switch, and its last run. Opened from the policy list; saves on its own. */
function CategoryDialog({ cat, limits, due, saving, confirming, onSave, onConfirm, onClose }) {
  const [draft, setDraft] = useState(() => editOf(cat));
  const id = `ret-${cat.key}`;
  const days = validDays(draft.retention_days, limits.max_retention_days);
  const invalid = days === null;
  const dirty = draft.retention_days !== String(cat.retention_days)
    || Number(draft.every_hours) !== cat.every_hours || draft.enabled !== cat.enabled;
  const fate = FATE[cat.fate] || { tone: 'neutral', label: cat.fate };
  const patch = (p) => setDraft((d) => ({ ...d, ...p }));

  return (
    <Modal open onClose={() => { if (!saving) onClose(); }} title={cat.label} description={cat.description}
           maxWidth={680}>
      <div className="stack gap-4">
        <div className="row row-wrap gap-2">
          <Badge tone={fate.tone}>{fate.label}</Badge>
          {cat.held?.length > 0 && <Badge tone="warn" dot>Awaiting confirmation</Badge>}
          {!cat.enabled && <Badge tone="neutral">Off schedule</Badge>}
        </div>

        {cat.caution && (
          <div className="notice notice-warn mb-0" role="note">
            <Icon name="alert" />
            <div className="notice-body">{cat.caution}</div>
          </div>
        )}

        <HeldNotice cat={cat} onConfirm={onConfirm} confirming={confirming} />

        <section aria-labelledby={`${id}-covers`}>
          <h2 id={`${id}-covers`} className="section-title">What it covers</h2>
          <div className="row row-wrap gap-2">
            {cat.members.map((m) => <Badge key={m.table} tone="outline" title={m.table}>{m.label}</Badge>)}
          </div>
          {due && <p className="mt-3 mb-0">Next run: <DueText due={due} /></p>}
        </section>

        <section aria-labelledby={`${id}-settings`}>
          <h2 id={`${id}-settings`} className="section-title">Settings</h2>
          <div className="form-row">
            <Field label="Keep for" htmlFor={`${id}-days`}
                   error={invalid ? `A whole number of days, 1 to ${limits.max_retention_days}` : null}
                   hint={invalid ? null : windowHint(cat, days)}>
              <div className="input-affix">
                <input id={`${id}-days`} type="number" min="1" max={limits.max_retention_days} step="1"
                       inputMode="numeric" className="form-input num" style={{ paddingLeft: 10, paddingRight: 48 }}
                       value={draft.retention_days} aria-invalid={invalid || undefined}
                       onChange={(e) => patch({ retention_days: e.target.value })} />
                <span className="affix-end text-sm text-muted" style={{ right: 12 }} aria-hidden="true">days</span>
              </div>
            </Field>
            <Field label="Runs" htmlFor={`${id}-every`}
                   hint={cat.next_run_at ? `Next run ${formatWhen(cat.next_run_at)}` : 'Not scheduled'}>
              <select id={`${id}-every`} className="form-input" value={draft.every_hours}
                      onChange={(e) => patch({ every_hours: Number(e.target.value) })}>
                {everyOptions(cat.every_hours).map(([h, label]) => <option key={h} value={h}>{label}</option>)}
              </select>
            </Field>
          </div>
          <Switch checked={draft.enabled} onChange={(v) => patch({ enabled: v })} label="On schedule"
                  description={draft.enabled ? 'Runs on its own, checked every hour.'
                    : 'Runs only when started by hand from this page.'} />
        </section>

        <section aria-labelledby={`${id}-last`}>
          <h2 id={`${id}-last`} className="section-title">Last run</h2>
          <LastRun run={cat.last_run} skip={cat.last_skip} />
        </section>
      </div>

      <div className="modal-footer">
        <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
        <button type="button" className="btn btn-primary" disabled={!dirty || invalid || saving}
                onClick={() => onSave(draft)}>
          {saving ? <><span className="spinner-sm" /> Saving…</> : 'Save'}
        </button>
      </div>
    </Modal>
  );
}

// A run this page started, still to show: its category has no run since `since` (ms), or
// the run is going. `since` is the browser's clock and `started_at` the server's, hence
// the slack.
const CLOCK_SLACK_MS = 30 * 1000;

function awaiting(cat, since) {
  if (since == null) return false;
  if (cat.last_run?.status === 'running') return true;
  // A skip answers the click too: the page shows why the run did not happen.
  const newest = Math.max(...[cat.last_run, cat.last_skip].filter(Boolean)
    .map((r) => Date.parse(r.started_at)), -Infinity);
  return newest < since - CLOCK_SLACK_MS;
}

function UserState({ user }) {
  if (user.deleted_at) {
    return (
      <Badge tone="danger" title={formatWhen(user.deleted_at)}>
        Deleted {formatDay(user.deleted_at)}
      </Badge>
    );
  }
  return user.is_active
    ? <Badge tone="ok" dot>Active</Badge>
    : <Badge tone="neutral" dot>Inactive</Badge>;
}

/* ──────────────────────────────────────────────────────────────────────────
   On-demand single-user wipe.
   Reuses the existing DELETE /api/users/:id?delete_data=true endpoint, the
   permanent form of the Users page's delete.
   Soft-deleted users are listed too so admins can also wipe residual data.
   Folded until asked for: the list is up to 1000 users, and it is the rarest task here.
────────────────────────────────────────────────────────────────────────── */

function WipeUser({ reloadKey }) {
  const [expanded, setExpanded] = useState(false);
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(null);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const [confirmText, setConfirmText] = useState('');
  const [open, setOpen] = useState(false);
  const [wiping, setWiping] = useState(false);

  const loadUsers = useCallback(async () => {
    setUsersLoading(true);
    try {
      // /api/users returns { users: [...], total, page, limit }: paginated.
      // Pull a generous page so the search box can find any user without a round
      // trip per keystroke. include_deleted=true so admins can also wipe residual
      // data from soft-deleted accounts.
      const r = await apiFetch('/api/users?limit=1000&include_deleted=true');
      if (!r.ok) { setUsersError(true); return; }
      const data = await r.json().catch(() => null);
      if (Array.isArray(data)) {
        setUsers(data);
        setTotal(null);
      } else if (data && Array.isArray(data.users)) {
        setUsers(data.users);
        setTotal(Number.isFinite(data.total) ? data.total : null);
      } else {
        setUsers([]);
        setTotal(null);
      }
      setUsersError(false);
    } catch {
      setUsersError(true);
    } finally {
      setUsersLoading(false);
    }
  }, []);

  // `reloadKey` moves after every sweep run from this page. The sweep hard-deletes inactive
  // users, and a row left behind here would offer to wipe someone who is already gone.
  useEffect(() => { if (expanded) loadUsers(); }, [expanded, loadUsers, reloadKey]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) =>
      (u.username || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q));
  }, [users, search]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = Math.min(page, pageCount);
  const visible = filtered.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);
  const deletedCount = users.filter((u) => u.deleted_at).length;
  // The list is one request capped at 1000, and search runs over what came back, so a
  // user past the cap cannot be found here. Say so instead of looking like "no match".
  const truncated = total != null && total > users.length;

  const onSearch = (value) => {
    setSearch(value);
    setPage(1);
  };

  const openConfirm = (user) => {
    setSelected(user);
    setConfirmText('');
    setOpen(true);
  };

  const wipe = async () => {
    if (!selected) return;
    setWiping(true);
    try {
      const data = await callApi(
        `/api/users/${selected.id}?delete_data=true`,
        { method: 'DELETE' },
        { successMsg: `User ${selected.username} wiped`, errorMsg: 'Wipe failed' },
      );
      if (data) {
        const wipedId = selected.id;
        setOpen(false);
        setSelected(null);
        setUsers((us) => us.filter((u) => u.id !== wipedId));
        setTotal((t) => (t == null ? t : t - 1));
      }
    } finally {
      setWiping(false);
    }
  };

  let countLabel = null;
  if (!usersLoading && !usersError) {
    if (truncated) {
      countLabel = `Only the first ${users.length.toLocaleString()} of ${total.toLocaleString()} users are listed`;
    } else {
      const shown = search.trim() ? `${filtered.length} of ${users.length}` : `${users.length}`;
      countLabel = `${shown} ${users.length === 1 ? 'user' : 'users'}`
        + (deletedCount ? ` · ${deletedCount} deleted` : '');
    }
  }

  let body;
  if (usersLoading) {
    body = <TableSkeleton rows={5} cols={5} label="Loading users" />;
  } else if (usersError) {
    body = (
      <EmptyState
        icon="alertCircle"
        title="The user list did not load"
        action={(
          <button type="button" className="btn btn-secondary" onClick={loadUsers}>
            <Icon name="refresh" /> Try again
          </button>
        )}
      >
        Nobody can be wiped from here until it does.
      </EmptyState>
    );
  } else if (users.length === 0) {
    body = (
      <EmptyState icon="users" title="No users on this environment">
        Nothing to wipe.
      </EmptyState>
    );
  } else if (filtered.length === 0) {
    body = (
      <EmptyState
        icon="search"
        title={`No users match “${search.trim()}”`}
        action={<button type="button" className="btn btn-secondary" onClick={() => onSearch('')}>Clear search</button>}
      >
        Search matches the username and the email address.
      </EmptyState>
    );
  } else {
    body = (
      <div className="table-container">
        <table className="table table-stack">
          <thead>
            <tr>
              <th>User</th>
              <th>Tier</th>
              <th>State</th>
              <th>Last active</th>
              <th><span className="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {visible.map((u) => (
              <tr key={u.id}>
                <td data-label="User">
                  {/* max-width on a <td> is ignored by table layout; on a block inside it,
                      it holds, so a long email truncates instead of widening the column. */}
                  <div style={{ maxWidth: 260, minWidth: 0 }}>
                    <span className="cell-primary">{u.username}</span>
                    <span className="cell-sub truncate" title={u.email || undefined}>
                      #{u.id}{u.email ? ` · ${u.email}` : ''}
                    </span>
                  </div>
                </td>
                <td data-label="Tier">
                  {u.tier_name
                    ? <Badge tone="accent">{u.tier_name}</Badge>
                    : <span className="text-faint">No tier</span>}
                </td>
                <td data-label="State"><UserState user={u} /></td>
                <td data-label="Last active" className="nowrap">
                  {u.last_active_at
                    ? <span className="num" title={formatWhen(u.last_active_at)}>{formatDay(u.last_active_at)}</span>
                    : <span className="text-faint">Never</span>}
                </td>
                <td data-label="Actions">
                  <div className="row row-end">
                    <button
                      type="button"
                      className="btn btn-danger-soft btn-sm"
                      onClick={() => openConfirm(u)}
                      aria-label={`Wipe ${u.username}`}
                    >
                      <Icon name="trash" /> Wipe
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="card-section">
      <div className="row-wrap between gap-4">
        <div className="min-w-0" style={{ flex: '1 1 360px' }}>
          <h3 id="dg-wipe" className="text-base fw-600">Wipe a user now</h3>
          <p className="text-sm text-muted mt-1">
            Skips the inactive-user grace period and deletes one user permanently. Soft-deleted
            users are listed too, so what they left behind can be wiped.
          </p>
        </div>
        <button type="button" className="btn btn-danger-soft" onClick={() => setExpanded((v) => !v)}
                aria-expanded={expanded} aria-controls="dg-wipe-list">
          <Icon name={expanded ? 'chevronUp' : 'users'} /> {expanded ? 'Hide the users' : 'Choose a user'}
        </button>
      </div>

      {expanded && (
        <div id="dg-wipe-list" className="card-inset mt-4" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="toolbar" style={{ padding: 'var(--sp-3) var(--sp-4)', margin: 0 }}>
            <SearchInput
              value={search}
              onChange={onSearch}
              placeholder="Filter by username or email…"
              label="Filter users by username or email"
            />
            <div className="toolbar-spacer" />
            {countLabel && (
              <span className={cx('text-sm num', truncated ? 'text-warn' : 'text-muted')}>{countLabel}</span>
            )}
          </div>
          {body}
          {!usersLoading && !usersError && (
            <div style={{ padding: '0 var(--sp-4) var(--sp-3)' }}>
              <Pagination page={current} pageSize={PAGE_SIZE} total={filtered.length} onPage={setPage} noun="users" />
            </div>
          )}
        </div>
      )}

      <Modal
        open={open}
        onClose={() => !wiping && setOpen(false)}
        title={selected ? `Wipe ${selected.username} permanently?` : 'Wipe user'}
        maxWidth={560}
      >
        {selected && (
          <>
            <p>
              This ends <strong>{selected.username}</strong>&apos;s grace period now, and it
              cannot be undone:
            </p>
            <ul className="stack gap-2 mt-3">
              <li className="row row-top gap-2">
                <Icon name="bot" className="text-danger" />
                <span>The agent is stopped and removed.</span>
              </li>
              <li className="row row-top gap-2">
                <Icon name="folder" className="text-danger" />
                <span>The workspace is deleted, with everything the agent saved in it.</span>
              </li>
              <li className="row row-top gap-2">
                <Icon name="database" className="text-danger" />
                <span>
                  The database row is removed, so the account cannot be restored. The user is
                  not billed further.
                </span>
              </li>
              <li className="row row-top gap-2">
                <Icon name="barChart" className="text-muted" />
                <span>Metrics are folded into the daily rollups first, so dashboard charts stay continuous.</span>
              </li>
            </ul>
            {selected.deleted_at && (
              <p className="text-muted mt-3">
                Already soft-deleted on {formatDay(selected.deleted_at)}: this removes what was
                left behind.
              </p>
            )}
            <Field
              label={<>Type <code>{selected.username}</code> to confirm</>}
              htmlFor="dg-wipe-confirm"
              className="mt-4 mb-0"
            >
              <input
                id="dg-wipe-confirm"
                type="text"
                className="form-input"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                autoFocus
                autoComplete="off"
                spellCheck={false}
              />
            </Field>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setOpen(false)} disabled={wiping}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={wipe}
                disabled={wiping || confirmText !== selected.username}
              >
                {wiping ? <><span className="spinner-sm" /> Wiping…</> : 'Wipe permanently'}
              </button>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}

export default function DataGovernance() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [editing, setEditing] = useState(null);
  // Runs started from this page: {category key: ms}, `all` for "run every category".
  const [pending, setPending] = useState({});
  const [confirming, setConfirming] = useState({});
  const [pollUntil, setPollUntil] = useState(0);
  const [runsFinished, setRunsFinished] = useState(0);
  const [confirm, confirmDialog] = useConfirm();
  const polling = useRef(false);

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    try {
      const resp = await apiFetch('/api/retention/categories');
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      setData(await resp.json());
      setLoadError(false);
    } catch {
      setLoadError(true);
    } finally {
      if (!quiet) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const categories = useMemo(() => data?.categories || [], [data]);
  const anyRunning = categories.some((c) => c.last_run?.status === 'running');
  const waitingFor = (cat) => Date.now() < pollUntil
    && awaiting(cat, pending[cat.key] ?? (cat.enabled ? pending.all : undefined));
  const waiting = categories.some(waitingFor);

  // While a run is going, or one started here has not shown up yet, refresh every few
  // seconds so the result appears without a reload.
  useEffect(() => {
    if (!anyRunning && !waiting) {
      if (polling.current) {
        polling.current = false;
        setRunsFinished((n) => n + 1);
      }
      return undefined;
    }
    polling.current = true;
    const t = setTimeout(() => load({ quiet: true }), POLL_MS);
    return () => clearTimeout(t);
  }, [anyRunning, waiting, data, load]);

  const save = async (cat, draft) => {
    const days = validDays(draft.retention_days, data.limits.max_retention_days);
    if (days === null) {
      toast.error('The window must be a whole number of days');
      return;
    }
    const shorter = days < cat.retention_days;
    if (shorter && !await confirm({
      title: `Shorten ${cat.label} to ${days} days?`,
      body: (
        <p>
          The next run deletes everything older than {formatDay(Date.now() - days * DAY_MS)}
          {cat.fate === 'aggregate' || cat.fate === 'compact' ? ', after rolling it into the daily aggregates' : ''}.
          {cat.fate === 'wipe' && <> What is past the new window is <strong>hard-deleted</strong>.</>} This
          cannot be undone.
        </p>
      ),
      confirmLabel: 'Shorten the window',
    })) return;
    setSaving((s) => ({ ...s, [cat.key]: true }));
    const body = await callApi(`/api/retention/categories/${cat.key}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ retention_days: days, every_hours: Number(draft.every_hours), enabled: draft.enabled }),
    }, { successMsg: 'Saved', errorMsg: 'Save failed' });
    setSaving((s) => ({ ...s, [cat.key]: false }));
    if (body) {
      setEditing(null);
      setPreview(null);
      await load({ quiet: true });
    }
  };

  const run = async (cat) => {
    if (cat.fate === 'wipe' && !await confirm({
      title: `Run ${cat.label} now?`,
      body: (
        <p>
          {cat.description} The window is {cat.effective_days} days. This cannot be undone.
        </p>
      ),
      confirmLabel: 'Run it now',
    })) return;
    const at = Date.now();
    const started = await callApi(`/api/retention/categories/${cat.key}/run`, { method: 'POST' },
      { successMsg: `${cat.label}: started`, errorMsg: 'Could not start the run' });
    if (started) {
      setPending((p) => ({ ...p, [cat.key]: at }));
      setPollUntil(at + POLL_FOR_MS);
    }
  };

  const confirmCategory = async (cat) => {
    const held = cat.held || [];
    const counts = preview?.categories?.[cat.key];
    const due = cat.key === 'deleted_accounts'
      ? counts?.deleted_records
      : held.reduce((n, h) => (typeof counts?.tables?.[h.table] === 'number' ? n + counts.tables[h.table] : n), 0);
    if (!await confirm({
      title: `Let ${cat.label} sweep ${listOf(held.map((h) => h.label))}?`,
      body: (
        <>
          <p>
            From its next run, {held.length === 1 ? 'this table follows' : 'these tables follow'} the
            category&apos;s window of {cat.effective_days} days, like the rest of it: anything older is deleted.
            The per-table settings never removed these rows, so this is the first time any of them go.
          </p>
          <p>
            {counts
              ? <>The last count found <strong className="num">{(due || 0).toLocaleString()}</strong> {due === 1 ? 'row' : 'rows'} past the window.</>
              : 'Count what is due first to see how many rows the next run removes.'}
            {' '}Once a run has removed them, it cannot be undone.
          </p>
        </>
      ),
      confirmLabel: 'Confirm',
    })) return;
    setConfirming((s) => ({ ...s, [cat.key]: true }));
    const body = await callApi(`/api/retention/categories/${cat.key}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ confirm: true }),
    }, { successMsg: `${cat.label}: confirmed`, errorMsg: 'Could not confirm' });
    setConfirming((s) => ({ ...s, [cat.key]: false }));
    if (body) await load({ quiet: true });
  };

  const runAll = async () => {
    if (!await confirm({
      title: 'Run every category now?',
      body: (
        <p>
          Each category that is on schedule runs once, with its saved window: old activity logs are
          rolled into the daily aggregates and deleted, and accounts past their window are{' '}
          <strong>hard-deleted</strong>. Tables waiting for a confirmation are left alone. It cannot be undone.
        </p>
      ),
      confirmLabel: 'Run every category',
    })) return;
    const at = Date.now();
    const started = await callApi('/api/retention/sweep', { method: 'POST' },
      { successMsg: 'Started: every category on schedule is running', errorMsg: 'Could not start the sweep' });
    if (started) {
      setPending({ all: at });
      setPollUntil(at + POLL_FOR_MS);
    }
  };

  const openPreview = async () => {
    setPreviewing(true);
    try {
      const resp = await apiFetch('/api/retention/preview');
      if (!resp.ok) { toast.error('Preview failed'); return; }
      setPreview(await resp.json());
    } catch {
      toast.error('Preview failed');
    } finally {
      setPreviewing(false);
    }
  };

  const countButton = (
    <button type="button" className="btn btn-secondary" onClick={openPreview} disabled={previewing || !data}>
      {previewing ? <span className="spinner-sm" /> : <Icon name={preview ? 'refresh' : 'eye'} />}
      {preview ? 'Count again' : 'Count what is due'}
    </button>
  );
  const header = (
    <PageHeader description="How long each category of data is kept, how often it is cleaned, and what happens to it."
                actions={countButton}>
      <h1 className="page-title">Data governance</h1>
    </PageHeader>
  );

  if (loading && !data && !loadError) {
    return (
      <div>
        {header}
        <CardSkeleton lines={8} />
        <CardSkeleton lines={3} />
      </div>
    );
  }

  const onSchedule = categories.filter((c) => c.enabled).length;
  const nextRun = categories.map((c) => c.next_run_at).filter(Boolean).sort()[0];
  const held = categories.filter((c) => c.held?.length);
  const editingCat = categories.find((c) => c.key === editing) || null;

  return (
    <div>
      {header}

      {loadError && (
        <div className="notice notice-danger" role="alert">
          <Icon name="alertCircle" />
          <div className="notice-body">
            <div className="notice-title">The retention categories did not load</div>
            {data
              ? 'The values below are from the last successful load and may be out of date.'
              : 'The windows and the last runs are unknown until they do.'}
          </div>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => load()} disabled={loading}>
            {loading ? <span className="spinner-sm" /> : <Icon name="refresh" />}
            Try again
          </button>
        </div>
      )}

      {held.length > 0 && (
        <div className="notice notice-warn" role="status">
          <Icon name="alert" />
          <div className="notice-body">
            <div className="notice-title">
              {held.length === 1 ? '1 category waits' : `${held.length} categories wait`} for your confirmation
            </div>
            {listOf(held.map((c) => c.label))} {held.length === 1 ? 'holds' : 'hold'} tables that were never
            cleaned before. Count what is due, then open the category to confirm.
          </div>
        </div>
      )}

      {data && (
        <section className="card card-flush" aria-labelledby="dg-categories">
          <div className="card-header">
            <div className="min-w-0">
              <h2 id="dg-categories" className="card-title">Retention policies</h2>
              <p className="card-subtitle">
                {categories.length} categories, {onSchedule === categories.length ? 'all' : onSchedule} on
                schedule{nextRun ? `. Next run ${formatShort(nextRun)}` : ''}. Runs are also listed on Platform, Jobs.
              </p>
            </div>
            {preview && (
              <span className="text-sm text-muted">Counted {formatShort(preview.counted_at)} with the saved windows</span>
            )}
          </div>
          <div className="table-container">
            <table className="table table-stack">
              <thead>
                <tr>
                  <th>Category</th>
                  <th>Keeps</th>
                  <th>At the end</th>
                  <th>Runs</th>
                  <th>Last run</th>
                  {preview && <th>Next run removes</th>}
                  <th><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {categories.map(cat => {
                  const fate = FATE[cat.fate] || { tone: 'neutral', label: cat.fate };
                  const running = cat.last_run?.status === 'running' || waitingFor(cat);
                  const allHeld = (cat.held || []).length === cat.members.length;
                  return (
                    <tr key={cat.key}>
                      <td data-label="Category" className="wrap">
                        <div style={{ maxWidth: 340, minWidth: 0 }}>
                          <span className="row row-wrap gap-2">
                            <button type="button" className="btn btn-link cell-primary" onClick={() => setEditing(cat.key)}>
                              {cat.label}
                            </button>
                            {cat.held?.length > 0 && <Badge tone="warn" dot>Awaiting confirmation</Badge>}
                            {cat.caution && <Icon name="alert" size={14} className="text-warn" label={cat.caution} />}
                          </span>
                          <span className="cell-sub">{cat.description}</span>
                        </div>
                      </td>
                      <td data-label="Keeps">
                        <span className="num fw-500">{cat.effective_days} days</span>
                        <span className="cell-sub">{sinceText(cat)}</span>
                      </td>
                      <td data-label="At the end"><Badge tone={fate.tone}>{fate.label}</Badge></td>
                      <td data-label="Runs">
                        {cat.enabled
                          ? <span>{everyLabel(cat.every_hours)}</span>
                          : <Badge tone="neutral">Off schedule</Badge>}
                        <span className="cell-sub" style={{ whiteSpace: 'nowrap' }}>
                          {cat.next_run_at ? `next ${formatShort(cat.next_run_at)}` : 'only by hand'}
                        </span>
                      </td>
                      <td data-label="Last run">
                        {running ? <Badge tone="info" dot pulse>Running</Badge>
                          : <LastRunCell run={cat.last_run} skip={cat.last_skip} />}
                      </td>
                      {preview && (
                        <td data-label="Next run removes"><DueText due={dueOf(cat, preview.categories?.[cat.key])} /></td>
                      )}
                      <td data-label="Actions">
                        <div className="row gap-1 row-end">
                          <button type="button"
                                  className={cx('btn btn-sm', cat.fate === 'wipe' ? 'btn-danger-soft' : 'btn-secondary')}
                                  disabled={running || allHeld} onClick={() => run(cat)}
                                  aria-label={`Run ${cat.label} now`}
                                  title={allHeld ? 'Confirm the category first' : undefined}>
                            {running ? <span className="spinner-sm" /> : <Icon name="play" />} Run now
                          </button>
                          <button type="button" className="btn btn-ghost btn-sm btn-icon" onClick={() => setEditing(cat.key)}
                                  aria-label={`Edit ${cat.label}`} title="Edit">
                            <Icon name="edit" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {data && (
        <section className="card danger-zone" aria-labelledby="dg-danger">
          <div className="card-header">
            <div className="min-w-0">
              <h2 id="dg-danger" className="card-title">Danger zone</h2>
              <p className="card-subtitle">Both actions delete data permanently. Neither can be undone.</p>
            </div>
          </div>
          <div className="card-section">
            <div className="row-wrap between gap-4">
              <div className="min-w-0" style={{ flex: '1 1 360px' }}>
                <h3 id="dg-run" className="text-base fw-600">Run every category now</h3>
                <p className="text-sm text-muted mt-1">
                  Runs every scheduled category once, now. Accounts past their window
                  are <strong>hard-deleted</strong>.
                </p>
              </div>
              <button type="button" className="btn btn-danger" onClick={runAll} disabled={anyRunning || waiting}>
                {anyRunning || waiting
                  ? <><span className="spinner-sm" /> Running…</>
                  : <><Icon name="play" /> Run every category</>}
              </button>
            </div>
          </div>
          <WipeUser reloadKey={runsFinished} />
        </section>
      )}

      {editingCat && (
        <CategoryDialog
          key={editingCat.key}
          cat={editingCat}
          limits={data.limits}
          due={dueOf(editingCat, preview?.categories?.[editingCat.key])}
          saving={!!saving[editingCat.key]}
          confirming={!!confirming[editingCat.key]}
          onSave={(draft) => save(editingCat, draft)}
          onConfirm={() => confirmCategory(editingCat)}
          onClose={() => setEditing(null)}
        />
      )}

      {confirmDialog}
    </div>
  );
}
